/* eslint-disable prettier/prettier */
import React, { type JSX } from 'react';
import { useConfirm, useDashboardCommonHandlers } from '@fiji/common/src/hooks';
import { FormControl, Select, MenuItem, Stack, InputLabel } from '@mui/material';
import { CustomModal, ModalActions } from 'components/CustomModal';
import { CustomTransComponent } from 'components';
import { useParams } from 'react-router-dom';
import { AddConfirmationModal } from './AddConfirmationModal';

type ModalProps = {
    widgetID?: string;
    'data-testid'?: string;
    onConfirmCommand?: (data: any, widgetId: string) => void;
};

export const AddConfirmationForFaultsWarnings = React.forwardRef(
    ({ widgetID, 'data-testid': dataTestId, onConfirmCommand }: ModalProps, ref): JSX.Element => {
        const { deviceId } = useParams();
        const [widgetData, setWidgetData] = React.useState<any>(null);
        const [title, setTitle] = React.useState<any>(null);
        const [selectedAction, setSelectedAction] = React.useState<any>(null);
        const { handleDeviceCommands } = useDashboardCommonHandlers(deviceId);
        const detailsModalRef: any = React.useRef(null);

        const { isVisible, onClick, onCancel, onConfirm } = useConfirm(() => {
            const channelActionSelected = widgetData?.find((item: any) => item.displayName === selectedAction);
            if (channelActionSelected?.isConfirm) {
                detailsModalRef?.current?.handleModalAction(true, channelActionSelected);
            } else if (onConfirmCommand) {
                onConfirmCommand(channelActionSelected, widgetID!);
            } else {
                void handleDeviceCommands(channelActionSelected, widgetID);
            }
            onCancel();
        });

        const handleModalAction = (action: boolean, data?: any): void => {
            if (action) {
                setWidgetData(data);
                setTitle(data[0]?.channelDisplayName);
                setSelectedAction(data[0]?.displayName);
                onClick(data);
                return;
            }
            onCancel();
        };

        React.useImperativeHandle(
            ref,
            () => ({
                handleModalAction: (action: boolean, data: any): void => handleModalAction(action, data),
            }),
            []
        );

        // Handle dropdown selection change
        const handleActionChange = (e: any): any => {
            const actionSelected = e.target.value;
            setSelectedAction(actionSelected);
        };

        const removeActions: ModalActions[] = [
            {
                testId: 'cancelBtn',
                key: 'cancel',
                label: <CustomTransComponent translationKey={'COMMON:CANCEL'} />,
                close: true,
                variant: 'outlined',
                handleClick: (): void => {
                    onCancel(true);
                },
            },
            {
                testId: 'commandName',
                key: 'commandName',
                label: `${selectedAction!}`,
                variant: 'contained',
                handleClick: onConfirm,
            },
        ];
        return (
            <>
                <CustomModal
                    type="primary"
                    header={`${title!}`}
                    isOpen={isVisible}
                    actions={removeActions}
                    noDividers
                    actionsDivider
                    data-testid={dataTestId ?? 'custom-modal'}
                >
                    <Stack>
                        <FormControl variant="filled" sx={{ mr: 2, ml: 2, mt: 2 }}>
                            <InputLabel id="demo-simple-select-filled-label">
                                <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:SELECT_ACTION'} />
                            </InputLabel>
                            <Select
                                className="custom-select"
                                labelId="demo-simple-select-helper-label"
                                id="demo-simple-select-helper"
                                variant="filled"
                                fullWidth
                                value={selectedAction}
                                onChange={handleActionChange}
                                sx={{ mt: 1 }}
                                MenuProps={{
                                    anchorOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'left',
                                    },
                                    PaperProps: {
                                        sx: { maxHeight: 200 },
                                    },
                                }}
                            >
                                {widgetData?.map((item: any) => (
                                    <MenuItem key={item.channelId} value={item.displayName}>
                                        {item?.displayName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </CustomModal>
                <AddConfirmationModal ref={detailsModalRef} widgetID={widgetID} onConfirmCommand={onConfirmCommand} />
            </>
        );
    }
);
